import { Button, Colors } from "@factorearth/component-library";
import { Draggable } from "@hello-pangea/dnd";
import React from "react";
import { FiTrash2, FiCopy } from "react-icons/fi";
import { MdOutlineDragIndicator } from "react-icons/md";
import styles from "./FormGroupOptionsItem.module.scss";
import { FormInstance } from "db/definitions";

interface Props {
	color: Colors;
	deleteFormInstance: (instance: FormInstance, index: number) => void;
	handleDuplicateFormInstance: (formInstance: FormInstance) => Promise<void>;
	index: number;
	instance: FormInstance;
	setInstanceToNav: (id: string) => void;
}

/**
 * Component to render a form instance in the form group options list.
 * @param {Object} props for the component as follows
 * @param {Object} color configuration data
 * @param {FormInstance} instance the form instance for the item in the list
 * @param {integer} index the index of the list item
 * @param {Function} setInstanceToNav callback to force a scroll to this form instance in the form group DOM
 * @param {Function} deleteFormInstance callback to delete this form instance from the form group.
 */
function FormGroupOptionsItem(props: Props) {
	const { color, instance, index, setInstanceToNav, deleteFormInstance, handleDuplicateFormInstance } = props;

	/**
	 * delete the form instance (already confirmed)
	 */
	function deleteForm() {
		deleteFormInstance(instance, index);
	}

	function duplicateForm() {
		handleDuplicateFormInstance(instance);
	}

	/**
	 * Setup the form instance to be scrolled to.
	 */
	function handleSetInstanceToNav() {
		setInstanceToNav(instance.id);
	}

	return (
		<Draggable key={instance.id} index={index} draggableId={instance.id}>
			{(provided, snapshot) => (
				<div
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					className={styles.draggable}
					id={instance.id}
					key={`${instance.id}--${index}`}
					onClick={handleSetInstanceToNav}>
					<div
						key={index}
						style={{ backgroundColor: color.background.primary, color: color.text.primary, border: `1px solid ${color.border.primary}` }}
						className={styles.wrapper}>
						<MdOutlineDragIndicator size={24} />
						<div className={styles.text}>
							{instance.formTemplate?.versionName
								? `${instance.formTemplate.template} - ${instance.formTemplate.versionName}`
								: instance.formTemplate?.template}
						</div>
						<Button
							colorPalette={color}
							data-test={"duplicate-form-instance-button"}
							variant="filled"
							onClick={duplicateForm}
							icon={<FiCopy size={12} />}
							style={{ height: "26px" }}
						/>
						<Button colorPalette={color} variant="destructive" onClick={deleteForm} icon={<FiTrash2 size={12} />} style={{ height: "26px" }} />
					</div>
				</div>
			)}
		</Draggable>
	);
}

export default FormGroupOptionsItem;
